<template>
    <div class="business-offer swiper-slide">
        <div class="business-offer-card">
            <div class="gallery-image-wrapper" v-if="paintCount > 0">
                <!-- Outer container reserves 16:10 aspect ratio to avoid layout shift -->
                <div class="gallery-image-outer">
                    <div class="gallery-image-inner">
                        <picture>
                            <source :srcset="'https://cdn.imagin.studio/getImage?customer=robinsandday&' + currentGalleryImage + '&zoomType=fullscreen&steering=right&width=800&angle=01&fileType=webp'" type="image/webp">
                            <img class="img-responsive" :src="'https://cdn.imagin.studio/getImage?customer=robinsandday&' + currentGalleryImage + '&zoomType=fullscreen&steering=right&width=800&angle=01&fileType=png'" :key="currentGalleryImage" @load="onGalleryImageLoad" @error="onGalleryImageError" itemprop="thumbnail" alt="">
                        </picture>
                    </div>

                    <!-- loading overlay shown while the image is loading -->
                    <div class="gallery-image-overlay" v-if="imageLoading">
                        <div class="gallery-spinner" role="status" aria-live="polite" aria-label="Loading image">
                            <svg viewBox="0 0 50 50" class="spinner-svg" xmlns="http://www.w3.org/2000/svg">
                                <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="search-results-vehicle__exclusive-offer-badge" v-if="exclusiveOffer"><img src="/images/business-and-fleet/star.svg" alt="Exclusive Offer">Exclusive Offer</div>
            </div>
            <div class="search-results-vehicle__swiper-container" v-if="paintCount > 1" @click.stop.prevent="">
                <div class="search-results-vehicle__swiper-btn search-results-vehicle__swiper-prev" ref="colour-prev"></div>
                <div class="swiper search-results-vehicle__colour-swiper" ref="colourSwiper">
                    <div class="swiper-wrapper">
                        <div class="swiper-slide" v-for="paint in vehicle.new_vehicle_paint_options" :key="'paint-' + paint.cap_paint_id">
                            <button type="button" @click.stop.prevent="setSelectedPaint(paint)" class="search-results-vehicle__paint-btn" :class="{ 'search-results-vehicle__paint-btn--active': selectedPaint.cap_paint_id == paint.cap_paint_id }" :title="paint.name">
                                <div class="search-results-vehicle__paint-btn-inner" :style="'background-color: ' + paint.primary_hex"></div>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="search-results-vehicle__swiper-btn search-results-vehicle__swiper-next" ref="colour-next"></div>
            </div>
            <div class="search-results-vehicle__no-img" v-if="imageLoadError || paintCount == 0">
                <picture>
                    <source srcset="/images/used/image-coming-soon.webp, /images/used/image-coming-soon@2x.webp 2x" type="image/webp" />
                    <img
                        class="search-results-vehicle__img img-responsive"
                        srcset="/images/used/image-coming-soon.jpg, /images/used/image-coming-soon@2x.jpg 2x"
                        itemprop="thumbnail"
                    />
                </picture>
            </div>
            <div class="business-offer-card__content">
                <div class="section">
                    <h2 class="business-offer-card__heading business-offer-card__text--dark-blue">{{ modelTitle }}</h2>
                    <div class="business-offer-card__subheading">{{ vehicle.bodystyle }} {{ vehicle.version }}</div>
                </div>

                <div class="section">
                    <div class="business-offer-card__pricing business-offer-card__pricing--bordered">
                        <div class="business-offer-card__text">Cash price only</div>
                        <sup class="business-offer-card__pricing--sup">Exc VAT & fees</sup>
                        <div class="business-offer-card__text business-offer-card__text--bold business-offer-card__text--dark-blue">£{{ vehicle.business_price_exc_vat }}</div>
                    </div>
                    <div class="business-offer-card__pricing business-offer-card__pricing--bordered">
                        <div class="business-offer-card__text">Per Month From*</div>
                        <div class="business-offer-card__text business-offer-card__text--bold business-offer-card__text--dark-blue">£{{ monthlyPrice }}</div>
                    </div>
                </div>
                <div class="business-offer-card__details">
                    <div class="business-offer-card__text">
                        <span class="business-offer-card__text--bold business-offer-card__text--dark-blue">{{ term }}</span> month term,
                        <span class="business-offer-card__text--bold business-offer-card__text--dark-blue">{{ annualMileage }}</span> miles p/a
                    </div>
                    <div class="business-offer-card__text">
                        <span class="business-offer-card__text--bold business-offer-card__text--dark-blue">£{{ initialRental }}</span> initial rental
                    </div>
                </div>
                <a :href="url">
                    <span class="btn btn--has-primary-icon btn--primary business-offer-card__btn">View offer</span>
                </a>
                <a class="link link--block link--has-icon link--primary" :href="`business/offers?manufacturer=${vehicle.manufacturer}&model=${vehicle.short_name}&type=${vehicle.type}`">View {{ modelTitle }} offers</a>
            </div>
        </div>
    </div>
</template>

<script>
import numberFormat from '../../../core/modules/number-format';
import Swiper, { Navigation } from 'swiper';

export default {
    name: "LatestBusinessOffer",
    props: {
        vehicle: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            imageIndex: 0,
            selectedPaint: {},
            currentStartIndex: 0,
            visibleItems: 4,
            imageLoading: false,
            imageLoadError: false,
            _imageLoadTimer: null,
            colourSwiper: null,
        };
    },
    computed: {
        hasNextImage() {
            if (this.paintCount <= 1) {
                return false;
            }
            return ((this.imageIndex + 1) < this.paintCount);
        },
        hasPreviousImage() {
            if (this.paintCount <= 1) {
                return false;
            }
            return (this.imageIndex > 0);
        },
        currentGalleryImage() {
            if (this.paintCount > 0) {
                return this.selectedPaint.url_identifier;
            }

            return "/images/used/image-coming-soon@2x.jpg";
        },

        paintCount() {
            if (this.vehicle.new_vehicle_paint_options) {
                return this.vehicle.new_vehicle_paint_options.length;
            }
            return 0;
        },
        modelTitle() {
            if (this.vehicle.fuel_type != 'Electric') {
                return this.vehicle.model_title;
            }
            if (this.vehicle.electric_model_title) {
                return this.vehicle.electric_model_title;
            }

            return this.vehicle.model_title;
        },
        hasMonthlyPricing() {
            return (typeof this.vehicle.cheapest_business_vehicle_finance_offer !== 'undefined' && this.vehicle.cheapest_business_vehicle_finance_offer !== null);
        },
        exclusiveOffer() {
            if (!this.hasMonthlyPricing) {
                return false;
            }
            return (this.vehicle.cheapest_business_vehicle_finance_offer.is_exclusive_offer == 1);
        },
        term() {
            return numberFormat(parseFloat(this.vehicle.cheapest_business_vehicle_finance_offer.term));
        },
        monthlyPrice() {
            return numberFormat(parseFloat(this.vehicle.cheapest_business_vehicle_finance_offer.monthly_rental));
        },
        initialRental() {
            return numberFormat(parseFloat(this.vehicle.cheapest_business_vehicle_finance_offer.initial_rental));
        },
        annualMileage() {
            return numberFormat(parseFloat(this.vehicle.cheapest_business_vehicle_finance_offer.annual_mileage));
        },
        url() {
            // Vehicle slug is a URL safe version of the vehicles model title, bodystyle, and version
            const slug = this.modelTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + this.vehicle.bodystyle.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + this.vehicle.version.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return `/${this.vehicle.brand}/business/offers/${this.vehicle.id}/${slug}`;
        }
    },
    methods: {
        setSelectedPaint(paint) {
            this.selectedPaint = paint;
            // if swiper is present, ensure the active slide is visible
            try {
                if (this.colourSwiper) {
                    const idx = this.vehicle.new_vehicle_paint_options.findIndex(p => p.cap_paint_id === paint.cap_paint_id);
                    if (idx >= 0) {
                        // slideTo expects slide index; our slides are in same order as options
                        this.colourSwiper.slideTo(idx);
                    }
                }
            } catch (e) { }
        },
        previousImage() {
            if (!this.hasPreviousImage) {
                return;
            }
            this.imageIndex--;
        },
        nextImage() {
            if (!this.hasNextImage) {
                return;
            }
            this.imageIndex++;
        },
        prevPaintOptions() {
            if (this.currentStartIndex > 0) {
                this.currentStartIndex -= this.visibleItems;
                this.currentStartIndex = Math.max(0, this.currentStartIndex);
            }
        },
        nextPaintOptions() {
            if (this.currentStartIndex + this.visibleItems < this.vehicle.new_vehicle_paint_options.length) {
                this.currentStartIndex += this.visibleItems;
                // Adjust index if we're close to the end
                if (this.currentStartIndex + this.visibleItems > this.vehicle.new_vehicle_paint_options.length) {
                    this.currentStartIndex = this.vehicle.new_vehicle_paint_options.length - this.visibleItems;
                }
            }
        },
        startImageLoadTimeout() {
            // clear any existing timer
            if (this._imageLoadTimer) {
                clearTimeout(this._imageLoadTimer);
                this._imageLoadTimer = null;
            }
            // start timeout to mark as error if image doesn't load in reasonable time
            const TIMEOUT_MS = 8000; // 8 seconds
            this._imageLoadTimer = setTimeout(() => {
                this.imageLoading = false;
                this.imageLoadError = true;
                this._imageLoadTimer = null;
            }, TIMEOUT_MS);
        },
        onGalleryImageLoad(event) {
            // image successfully loaded
            this.imageLoading = false;
            this.imageLoadError = false;
            if (this._imageLoadTimer) {
                clearTimeout(this._imageLoadTimer);
                this._imageLoadTimer = null;
            }
        },
        onGalleryImageError(event) {
            // image failed to load
            this.imageLoading = false;
            this.imageLoadError = true;
            if (this._imageLoadTimer) {
                clearTimeout(this._imageLoadTimer);
                this._imageLoadTimer = null;
            }
        },
    },
    watch: {
        // when the computed currentGalleryImage changes, start the loading state
        currentGalleryImage(newVal, oldVal) {
            // If there is no change or empty value, do nothing
            if (!newVal) {
                this.imageLoading = false;
                this.imageLoadError = false;
                return;
            }

            // Start loading overlay and timeout. The actual <img> has @load/@error handlers that will clear this.
            this.imageLoading = true;
            this.imageLoadError = false;
            this.startImageLoadTimeout();
        }
    },
    created() {
        if (this.paintCount > 0) {
            if (this.vehicle.cap_paint_id) {
                const foundPaint = this.vehicle.new_vehicle_paint_options.find(p => p.cap_paint_id === this.vehicle.cap_paint_id);
                if (foundPaint) {
                    this.selectedPaint = foundPaint;
                    this.imageIndex = this.vehicle.new_vehicle_paint_options.indexOf(foundPaint);
                    return;
                }
            }
            this.selectedPaint = this.vehicle.new_vehicle_paint_options[0];
        }
    },
    mounted() {
        // initialize colour picker swiper if there are multiple paints
        this.$nextTick(() => {
            try {
                if (this.paintCount > 1 && this.$refs['colourSwiper']) {
                    this.colourSwiper = new Swiper(this.$refs['colourSwiper'], {
                        modules: [Navigation],
                        grabCursor: true,
                        slidesPerView: "auto",
                        spaceBetween: 8,
                        speed: 300,
                        direction: "horizontal",
                        navigation: {
                            prevEl: this.$refs['colour-prev'],
                            nextEl: this.$refs['colour-next'],
                        },
                        on: {
                            slideChange: () => {
                                if (!this.$refs['colour-prev'] || !this.$refs['colour-next']) return;
                                if (this.colourSwiper.isBeginning) {
                                    this.$refs['colour-prev'].classList.add('search-results-vehicle__nav-button--disabled');
                                    this.$refs['colour-next'].classList.remove('search-results-vehicle__nav-button--disabled');
                                    return;
                                }
                                if (this.colourSwiper.isEnd) {
                                    this.$refs['colour-prev'].classList.remove('search-results-vehicle__nav-button--disabled');
                                    this.$refs['colour-next'].classList.add('search-results-vehicle__nav-button--disabled');
                                    return;
                                }
                                this.$refs['colour-prev'].classList.remove('search-results-vehicle__nav-button--disabled');
                                this.$refs['colour-next'].classList.remove('search-results-vehicle__nav-button--disabled');
                            }
                        }
                    });

                    // ensure current selected paint is visible
                    try {
                        const initialIdx = this.vehicle.new_vehicle_paint_options.findIndex(p => p.cap_paint_id === this.selectedPaint.cap_paint_id);
                        if (initialIdx >= 0) {
                            this.colourSwiper.slideTo(initialIdx);
                            return;
                        }
                    } catch (e) {}
                    // set initial disabled state
                    if (this.colourSwiper.isBeginning && this.$refs['colour-prev']) {
                        this.$refs['colour-prev'].classList.add('search-results-vehicle__nav-button--disabled');
                    }
                    if (this.colourSwiper.isEnd && this.$refs['colour-next']) {
                        this.$refs['colour-next'].classList.add('search-results-vehicle__nav-button--disabled');
                    }
                }
            } catch (e) {
                // silently fail if swiper not available
                console.info('colour swiper init failed', e);
            }
        });

        // if there's an initial gallery image, start the loading overlay until the <img> fires load/error
        if (this.paintCount > 0 && this.currentGalleryImage) {
            this.imageLoading = true;
            this.imageLoadError = false;
            this.startImageLoadTimeout();
        }
    }
}
</script>
